/**
 * Route table of §12.1 as data:
 *
 * ```
 * /login
 * /                     Homescreen
 * /notifications        Notification history (#48-#50)
 * /feed                 Global feed across every watched profile (#51); ?post=<id>
 * /saved                Saved-posts collection (#66); ?post=<id>
 * /watch                Posts to Comment — New Post Watching detections (#71); ?post=<id>
 * /content              Content Performance — imported LinkedIn analytics (#87-#93)
 *                                     ?mode=metrics|posts|comments (default metrics)
 *                                     ?post=<id> open post-preview popup (enriched rows only)
 * /view/:t/:id          View screen; ?mode=metrics|posts|feed|profile (default metrics)
 *                                     ?post=<id> open post-preview popup (deep-linkable)
 * ```
 */

import {
  CONTENT_MODES,
  SUBJECT_TYPES,
  VIEW_MODES,
  type ContentMode,
  type SubjectType,
  type ViewMode,
} from '@lw/shared';

export const LOGIN_PATH = '/login';
export const HOME_PATH = '/';
export const NOTIFICATIONS_PATH = '/notifications';
export const FEED_PATH = '/feed';
export const SAVED_PATH = '/saved';
export const WATCH_PATH = '/watch';
export const CONTENT_PATH = '/content';
export const VIEW_PATH_PATTERN = '/view/:t/:id';

/** Query param carrying the view mode — shared by `/view` and `/content` (§12.4). */
export const VIEW_MODE_PARAM = 'mode';
export const DEFAULT_VIEW_MODE: ViewMode = 'metrics';
export const DEFAULT_CONTENT_MODE: ContentMode = 'metrics';

/** Query param carrying the id of the open post-preview popup — what makes it deep-linkable. */
export const POST_PREVIEW_PARAM = 'post';

export function viewPath(t: SubjectType, id: number, mode: ViewMode = DEFAULT_VIEW_MODE): string {
  const base = `/view/${t}/${id}`;
  return mode === DEFAULT_VIEW_MODE ? base : `${base}?${VIEW_MODE_PARAM}=${mode}`;
}

export function parseSubjectType(raw: string | undefined): SubjectType | null {
  return SUBJECT_TYPES.find((candidate) => candidate === raw) ?? null;
}

/** Every id in a URL is a positive integer: no leading zeros, no sign, nothing past 2^53-1. */
function parseId(raw: string | null | undefined): number | null {
  if (raw === undefined || raw === null || !/^[1-9][0-9]*$/.test(raw)) return null;
  const id = Number(raw);
  return Number.isSafeInteger(id) ? id : null;
}

export function parseSubjectId(raw: string | undefined): number | null {
  return parseId(raw);
}

/**
 * `?post=` — a garbage value is inert rather than an error: no popup renders, and the param is
 * left where it is until the next open/close rewrites it (§12.1).
 */
export function parsePostParam(raw: string | null): number | null {
  return parseId(raw);
}

/**
 * The canonical deep link to a post: its **author's** view screen with the preview popup open.
 *
 * One link per post, whatever surface copied it — the global feed, a group's feed and the popup
 * itself all hand out the same string, and it matches the app link the Notion integration writes
 * server-side. Building it from the current URL instead would give `/feed?post=` here and a group
 * view's URL there, which are three addresses for one post.
 */
export function postDeepLink(postId: number, profileId: number): string {
  const url = new URL(`/view/profile/${String(profileId)}`, window.location.origin);
  url.searchParams.set(POST_PREVIEW_PARAM, String(postId));
  return url.toString();
}

/** Unknown/absent `?mode=` falls back to the default rather than 404-ing the route (§12.1). */
export function parseViewMode(raw: string | null | undefined): ViewMode {
  return VIEW_MODES.find((candidate) => candidate === raw) ?? DEFAULT_VIEW_MODE;
}

/** `/content?mode=` — same contract, its own three-word vocabulary (SPEC § Navigation). */
export function parseContentMode(raw: string | null | undefined): ContentMode {
  return CONTENT_MODES.find((candidate) => candidate === raw) ?? DEFAULT_CONTENT_MODE;
}

export function contentPath(mode: ContentMode = DEFAULT_CONTENT_MODE): string {
  return mode === DEFAULT_CONTENT_MODE
    ? CONTENT_PATH
    : `${CONTENT_PATH}?${VIEW_MODE_PARAM}=${mode}`;
}
